import cn from 'classnames'
import React from 'react'
import { Icon } from '../icon'
import { Typography } from '../typography'
import styles from './IconButton.module.css'
import { IconButtonComponent, IconButtonProps, IconButtonSize, IconButtonVariant } from './IconButton.types'

const SIZE_CLASS: Record<IconButtonSize, string> = {
  M: styles.sizeM,
  S: styles.sizeS,
  XS: styles.sizeXS,
}

const VARIANT_CLASS: Record<IconButtonVariant, string> = {
  'overlay-neutral': styles.variantOverlayNeutral,
  'transparent-neutral': styles.variantTransparentNeutral,
  'secondary': styles.variantSecondary,
}

const BASE_ICON_SIZES: Record<IconButtonSize, number> = { M: 24, S: 20, XS: 16 }
const TRANSPARENT_ICON_SIZES: Record<IconButtonSize, number> = { M: 32, S: 24, XS: 20 }

function IconButtonBase({
  ref,
  size: sizeProp,
  variant,
  type,
  glyph,
  title,
  disabled,
  className,
  onClick,
  as,
  ...rest
}: IconButtonProps<React.ElementType>) {
  const Component: React.ElementType = as || 'button'
  const size: IconButtonSize = sizeProp ?? 'M'
  const currentVariant: IconButtonVariant = variant ?? 'overlay-neutral'
  const iconSize = variant === 'transparent-neutral' ? TRANSPARENT_ICON_SIZES[size] : BASE_ICON_SIZES[size]
  const resolvedType = Component === 'button' ? type ?? 'button' : type

  return (
    <Component
      {...rest}
      ref={ref}
      type={resolvedType}
      className={cn(styles.iconButton, disabled && styles.disabled, className)}
      onClick={onClick}
      disabled={disabled}
    >
      <div className={cn(styles.iconWrapper, SIZE_CLASS[size], VARIANT_CLASS[currentVariant])}>
        <Icon glyph={glyph} size={iconSize} />
      </div>
      {title && (
        <Typography variant="body-xs-regular-tight" className={styles.title}>
          {title}
        </Typography>
      )}
    </Component>
  )
}

const IconButton: IconButtonComponent = IconButtonBase

export default IconButton
